import {
  DEFAULT_PDP_ADD_TO_CART_BUTTON_SELECTOR,
  DEFAULT_PRODUCT_FORM_SELECTOR,
} from "../selectors";

export interface ProductFormElements {
  productForm: HTMLElement;
  addToCartButton: HTMLButtonElement | HTMLInputElement;
}

// Helper function to calculate DOM distance between two nodes
function calculateDOMDistance(node1: Node, node2: Node): number {
  // Get the path from each node to the document root
  const path1 = getPathToRoot(node1);
  const path2 = getPathToRoot(node2);

  // Find the common ancestor
  let commonAncestorIndex = -1;
  const minLength = Math.min(path1.length, path2.length);

  for (let i = 0; i < minLength; i++) {
    if (path1[path1.length - 1 - i] === path2[path2.length - 1 - i]) {
      commonAncestorIndex = i;
    } else {
      break;
    }
  }

  // Calculate distance as the sum of steps from both nodes to their common ancestor
  const distance1 = path1.length - 1 - commonAncestorIndex;
  const distance2 = path2.length - 1 - commonAncestorIndex;

  return distance1 + distance2;
}

// Helper function to get path from node to document root
function getPathToRoot(node: Node): Node[] {
  const path: Node[] = [];
  let current: Node | null = node;

  while (current) {
    path.push(current);
    current = current.parentNode;
  }

  return path;
}

type PossibleProductFormElements = {
  form: HTMLElement;
  button: HTMLButtonElement | HTMLInputElement;
  distance: number;
};

export function getProductFormElements(
  tryFirstContainer: HTMLElement,
): ProductFormElements | null {
  // Get all product forms on the page
  const allProductForms = document.querySelectorAll(
    DEFAULT_PRODUCT_FORM_SELECTOR,
  );

  // Filter forms that have a valid add to cart button
  const validForms: Array<PossibleProductFormElements> = [];

  for (const form of allProductForms) {
    const addToCartButton = getAddToCartButton(form);

    if (addToCartButton) {
      const distance = calculateDOMDistance(tryFirstContainer, form);

      validForms.push({
        form: form as HTMLElement,
        button: addToCartButton,
        distance,
      });
    }
  }

  if (validForms.length === 0) {
    return null;
  }

  // Prioritize the closest form
  const sortedForms = validForms.sort((a, b) => a.distance - b.distance);
  const closest = sortedForms[0];

  if (!closest) {
    return null;
  }

  return { productForm: closest.form, addToCartButton: closest.button };
}

export function getAddToCartButton(
  form: Element,
): HTMLButtonElement | HTMLInputElement | null {
  return form.querySelector<HTMLButtonElement | HTMLInputElement>(
    DEFAULT_PDP_ADD_TO_CART_BUTTON_SELECTOR,
  );
}

export function isValidProductForm(form: HTMLElement): boolean {
  return (
    form.matches(DEFAULT_PRODUCT_FORM_SELECTOR) && !!getAddToCartButton(form)
  );
}

// Helper function to check if add to cart button is enabled
export function isAddToCartButtonEnabled(
  button: HTMLButtonElement | HTMLInputElement,
): boolean {
  return !button.disabled;
}
